/**
 * Castellan Security — Worker API Client
 *
 * Two API styles coexist during the Phase 2 KV → D1 migration:
 *
 * 1. LEGACY KV API — workerPost / workerGet
 *    - POST to root with { authAction, ... } body
 *    - Content-Type text/plain (Worker requirement)
 *    - Token passed inside the body as _token
 *    - Used by all the existing modals + sync until they're migrated
 *
 * 2. NEW D1 V1 API — workerPostV1 / workerGetV1 / workerPutV1
 *    - REST-style paths under /api/v1/*
 *    - Content-Type application/json
 *    - Token passed via Authorization: Bearer <token> header
 *    - Used by all newly migrated code
 *
 * Both helpers hit the same WORKER_URL. The Worker decides which
 * handler runs based on the path: /api/v1/* → D1 path, everything
 * else → legacy KV path.
 */

/* Vite replaces import.meta.env at build time — declare for TypeScript */
declare const __WORKER_URL__: string | undefined

// API endpoint resolution:
//   - On the staging host (staging.castellan-security.com) the app must call
//     ITS OWN origin so /api/v1/* hits the STAGING worker + staging D1. The
//     staging tenant + cad flag + cad_* tables live there, not in production.
//     Hardcoding production here meant the staging app fetched tenant config
//     from prod (which has no 'staging' tenant) and got locked-out defaults.
//   - Everywhere else (production, fom2, native Electron/Capacitor) keep the
//     main production domain. Tenant is identified via the X-Tenant-ID header.
export const WORKER_URL = (typeof window !== 'undefined' && window.location.hostname.startsWith('staging.'))
  ? window.location.origin
  : 'https://castellan-security.com'

// STAGING-ONLY tenant override: ?tenant=<id> forces the active tenant on the
// staging host so multiple test tenants (which share one staging subdomain) can
// be reached in-browser. Gated to the 'staging.' hostname — this branch never
// runs on production (castellan-security.com / fom2.*), so prod tenant
// resolution is completely unaffected.
if (typeof window !== 'undefined' && window.location.hostname.startsWith('staging.')) {
  const _t = new URLSearchParams(window.location.search).get('tenant')
  if (_t) localStorage.setItem('castellan_tenant', _t)
}

// Set tenant immediately at module load time — before any API call fires.
// Ensures X-Tenant-ID header is always sent correctly.
if (typeof window !== 'undefined' && !localStorage.getItem('castellan_tenant')) {
  const host = window.location.hostname
  const proto = window.location.protocol
  if (proto === 'file:' || proto === 'capacitor:' || (host === 'localhost' && !window.location.port)) {
    // Native app (Electron or Capacitor) — default to fom2 tenant
    localStorage.setItem('castellan_tenant', 'fom2')
  } else {
    const parts = host.split('.')
    if (parts.length >= 3 && parts[parts.length - 2] === 'castellan-security') {
      localStorage.setItem('castellan_tenant', parts[0])
    }
  }
}

function getToken(): string {
  try {
    const raw = localStorage.getItem('fom_auth_session')
    if (raw) return (JSON.parse(raw) as { token?: string }).token ?? ''
  } catch { /* silent */ }
  return ''
}

/* ── LEGACY KV API ─────────────────────────────────────────────────── */

/** POST — body JSON-stringified, Content-Type text/plain (Worker requirement) */
export async function workerPost<T = unknown>(
  payload: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(WORKER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`Worker POST → ${res.status}`)
  return res.json() as Promise<T>
}

/** GET — returns all cloud data keyed by fom_* localStorage keys */
export async function workerGet<T = unknown>(): Promise<T> {
  const token = getToken()
  const url = `${WORKER_URL}?t=${Date.now()}&token=${encodeURIComponent(token)}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Worker GET → ${res.status}`)
  return res.json() as Promise<T>
}

/* ── NEW D1 V1 API ────────────────────────────────────────────────── */

function v1Url(path: string): string {
  if (!path.startsWith('/api/v1/')) {
    throw new Error(`workerV1: path must start with /api/v1/ (got: ${path})`)
  }
  return `${WORKER_URL}${path}`
}

function v1Headers(token?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const t = token ?? getToken()
  if (t) headers['Authorization'] = `Bearer ${t}`
  // Send tenant slug if set by FindAgencyPage
  const tenant = localStorage.getItem('castellan_tenant')
  if (tenant) headers['X-Tenant-ID'] = tenant
  return headers
}

/** POST to /api/v1/* with JSON body and Bearer auth */
export async function workerPostV1<T = unknown>(
  path: string,
  body: Record<string, unknown> = {},
  token?: string,
): Promise<T> {
  const doFetch = () => fetch(v1Url(path), {
    method: 'POST',
    headers: v1Headers(token),
    body: JSON.stringify(body),
  })
  let res = await doFetch()
  if (res.status === 401 && !path.includes('/auth/refresh') && !path.includes('/auth/login')) {
    const retry = await retryWithRefresh(doFetch)
    if (retry) res = retry
  }
  if (!res.ok) throw new Error(`Worker POST ${path} → ${res.status}`)
  return res.json() as Promise<T>
}

/** GET from /api/v1/* with Bearer auth and optional query params */
export async function workerGetV1<T = unknown>(
  path: string,
  params?: Record<string, string>,
  token?: string,
): Promise<T> {
  let url = v1Url(path)
  if (params && Object.keys(params).length > 0) {
    const qs = new URLSearchParams(params).toString()
    url += `?${qs}`
  }
  const doFetch = () => fetch(url, {
    method: 'GET',
    headers: v1Headers(token),
  })
  let res = await doFetch()
  if (res.status === 401) {
    const retry = await retryWithRefresh(doFetch)
    if (retry) res = retry
  }
  if (!res.ok) throw new Error(`Worker GET ${path} → ${res.status}`)
  return res.json() as Promise<T>
}

/** PUT to /api/v1/* with JSON body and Bearer auth (used for profile updates) */
export async function workerPutV1<T = unknown>(
  path: string,
  body: Record<string, unknown> = {},
  token?: string,
): Promise<T> {
  const doFetch = () => fetch(v1Url(path), {
    method: 'PUT',
    headers: v1Headers(token),
    body: JSON.stringify(body),
  })
  let res = await doFetch()
  if (res.status === 401) {
    const retry = await retryWithRefresh(doFetch)
    if (retry) res = retry
  }
  if (!res.ok) throw new Error(`Worker PUT ${path} → ${res.status}`)
  return res.json() as Promise<T>
}

/* ── SHARED ───────────────────────────────────────────────────────────── */

/** Attempt token refresh then retry a failed request (called on 401) */
async function retryWithRefresh(originalFetch: () => Promise<Response>): Promise<Response | null> {
  try {
    const { useAuthStore } = await import('../stores/auth')
    const ok = await useAuthStore.getState().refreshAccessToken()
    if (!ok) return null
    // Retry the original request with the new token
    return await originalFetch()
  } catch { return null }
}

/** SHA-256 hash — used for password before sending to Worker */
export async function sha256(message: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(message),
  )
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}
